import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { Trash2 } from "lucide-react";
import { useTranslation } from "../../i18n";

/** Run states in which the campaign is still sending. */
const ACTIVE_RUN_STATES = new Set(["running", "stopping"]);

/**
 * Table listing all campaigns.
 *
 * Left column is the campaign's primary control, which depends on its kind:
 * a **one-shot** campaign (a broadcast, a VIP grant, a spend threshold) is
 * *launched*, so it gets a Launch button there in place of the enable toggle —
 * it has no schedule to enable, and offering both a toggle and a Run button
 * for the same action was confusing. A **scheduled** campaign keeps the
 * enable/disable toggle, since that is what arms its cron entry.
 *
 * Right column holds Edit, Test and Delete (plus Stop while a run is in
 * flight). Re-running a scheduled campaign on demand was removed with the
 * Run button: the toggle already decides whether it fires, and a manual run of
 * a live schedule is better done by editing the schedule and waiting. Edit
 * opens the settings in a modal rather than expanding a second form on the
 * page, which is what pushed the campaign log off the screen.
 *
 * `busyId` disables the actions for the campaign with an in-flight request,
 * preventing double-click double-sends. A running campaign shows a Stop
 * button — disabling the action instead would leave no way to halt a blast
 * already under way.
 */
export default function CampaignTable({
  campaigns,
  triggerTypes,
  selectedId,
  busyId = null,
  onSelect,
  onEdit,
  onToggle,
  onRun,
  onStop,
  onTest,
  onDelete,
}) {
  const { t } = useTranslation();

  const getLabel = (triggerType) => {
    const found = triggerTypes.find((tt) => tt.triggerType === triggerType);
    return found?.label || triggerType;
  };

  const isOneShot = (triggerType) => {
    const found = triggerTypes.find((tt) => tt.triggerType === triggerType);
    return found?.oneShot ?? false;
  };

  if (campaigns.length === 0) {
    return (
      <div className="text-c-gray-400 text-sm py-8 text-center">
        {t("campaign.noCampaigns")}
      </div>
    );
  }

  return (
    <div className="overflow-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-c-gray-400/30">
            <th className="text-left py-2 px-2 text-c-gray-400">
              {t("campaign.colStatus")}
            </th>
            <th className="text-left py-2 px-2 text-c-gray-400">
              {t("campaign.colName")}
            </th>
            <th className="text-left py-2 px-2 text-c-gray-400">
              {t("campaign.colType")}
            </th>
            <th className="text-right py-2 px-2 text-c-gray-400">
              {t("campaign.colActions")}
            </th>
          </tr>
        </thead>
        <tbody>
          {campaigns.map((c) => {
            const busy = busyId === c.id;
            const oneShot = isOneShot(c.triggerType);
            const runState = c.run?.runState;
            const running = Boolean(
              runState && ACTIVE_RUN_STATES.has(runState),
            );
            return (
              <tr
                key={c.id}
                className={`border-b border-c-gray-400/10 hover:bg-c-gray-500/50 focus:bg-c-gray-500/50 focus:outline-none ${
                  selectedId === c.id ? "bg-accent/10" : ""
                }`}
                tabIndex={0}
                onClick={() => onSelect(c.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onSelect(c.id);
                  }
                }}
              >
                <td className="py-2 px-2" onClick={(e) => e.stopPropagation()}>
                  {oneShot ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={busy || running}
                      onClick={() => onRun(c.id)}
                    >
                      {t("campaign.launch")}
                    </Button>
                  ) : (
                    <Switch
                      checked={c.enabled}
                      disabled={busy || running}
                      onCheckedChange={() => onToggle(c.id, !c.enabled)}
                    />
                  )}
                </td>
                <td className="py-2 px-2 truncate max-w-[200px]">
                  {c.name || <span className="text-c-gray-400">#{c.id}</span>}
                  {running && (
                    <span className="ml-2 text-accent text-xs">
                      {t(`campaign.runStateShort.${runState}`)}
                    </span>
                  )}
                </td>
                <td className="py-2 px-2">{getLabel(c.triggerType)}</td>
                <td
                  className="py-2 px-2 text-right"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex gap-1 justify-end">
                    {running && (
                      <Button
                        variant="destructive"
                        size="sm"
                        disabled={busy || runState === "stopping"}
                        onClick={() => onStop(c.id)}
                      >
                        {runState === "stopping"
                          ? t("campaign.stopping")
                          : t("campaign.stop")}
                      </Button>
                    )}
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={busy}
                      onClick={() => onEdit(c.id)}
                    >
                      {t("campaign.editBtn")}
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={busy}
                      onClick={() => onTest(c.id)}
                    >
                      {t("campaign.test")}
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      disabled={busy || running}
                      onClick={() => onDelete(c.id)}
                      // Icon-only: the previous "Удал." / "Del" label was
                      // truncated to fit the actions column, which read as
                      // broken text rather than a verb. The accessible name
                      // carries the full wording.
                      aria-label={t("campaign.delete")}
                      title={t("campaign.delete")}
                    >
                      <Trash2 size={16} aria-hidden="true" />
                    </Button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
